import { CheckCircle2 } from 'lucide-react';
import { useEffect, useState } from 'react';

interface FinishEventModalProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: () => void;
    eventName: string;
    eventDate?: string;
    eventVenue?: string;
    isFinishing?: boolean;
}

export function FinishEventModal({
    isOpen,
    onClose,
    onConfirm,
    eventName,
    eventDate,
    eventVenue,
    isFinishing = false,
}: FinishEventModalProps) {
    const [showIcon, setShowIcon] = useState(false);

    // Bloquear scroll del body cuando el modal está abierto
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => {
            document.body.style.overflow = '';
        };
    }, [isOpen]);

    // Animación del icono al abrir
    useEffect(() => {
        if (isOpen) {
            const timer = setTimeout(() => setShowIcon(true), 200);
            return () => clearTimeout(timer);
        } else {
            setShowIcon(false);
        }
    }, [isOpen]);

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center overflow-hidden">
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-black/50 backdrop-blur-sm"
                onClick={onClose}
            />

            {/* Modal */}
            <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-sm mx-4 overflow-hidden animate-in fade-in zoom-in-95 duration-300">
                {/* Header con fondo azul e icono */}
                <div className="relative h-44 bg-gradient-to-b from-blue-50 via-blue-50/80 to-white flex items-center justify-center overflow-hidden">
                    {/* Decoraciones (cruces y puntos) */}
                    <div className="absolute inset-0 pointer-events-none">
                        {/* Cruces decorativas */}
                        <span className="absolute top-8 left-12 text-blue-300 text-lg font-light">+</span>
                        <span className="absolute top-6 right-16 text-blue-300 text-sm font-light">+</span>
                        <span className="absolute top-16 right-10 text-blue-200 text-xs font-light">+</span>
                        <span className="absolute bottom-16 left-10 text-blue-200 text-xs font-light">+</span>
                        {/* Puntos decorativos */}
                        <span className="absolute top-10 left-20 w-1.5 h-1.5 bg-blue-200 rounded-full" />
                        <span className="absolute top-14 right-20 w-1 h-1 bg-blue-300 rounded-full" />
                        <span className="absolute bottom-20 left-16 w-1 h-1 bg-blue-200 rounded-full" />
                        <span className="absolute bottom-14 right-14 w-1.5 h-1.5 bg-blue-200 rounded-full" />
                    </div>

                    {/* Círculos animados concéntricos */}
                    <div className="absolute inset-0 flex items-center justify-center">
                        <div className={`absolute w-32 h-32 rounded-full border-2 border-blue-200/40 transition-all duration-700 ${showIcon ? 'scale-100 opacity-100' : 'scale-50 opacity-0'}`} />
                        <div className={`absolute w-24 h-24 rounded-full border-2 border-blue-300/50 transition-all duration-500 delay-100 ${showIcon ? 'scale-100 opacity-100' : 'scale-50 opacity-0'}`} />
                        <div className={`absolute w-16 h-16 rounded-full bg-blue-100/60 transition-all duration-300 delay-150 ${showIcon ? 'scale-100 opacity-100' : 'scale-50 opacity-0'}`} />
                    </div>

                    {/* Icono de check con animación */}
                    <div className={`relative w-14 h-14 rounded-full bg-blue-500 flex items-center justify-center shadow-lg shadow-blue-500/30 transition-all duration-500 ${showIcon ? 'scale-100 opacity-100' : 'scale-0 opacity-0'}`}>
                        <CheckCircle2 className={`w-7 h-7 text-white transition-all duration-300 delay-300 ${showIcon ? 'scale-100 opacity-100' : 'scale-0 opacity-0'}`} strokeWidth={2} />
                    </div>
                </div>

                {/* Contenido */}
                <div className="px-6 pb-6">
                    {/* Título */}
                    <h2 className="text-xl font-bold text-slate-900 text-center mb-2">
                        ¿Finalizar evento?
                    </h2>

                    {/* Subtítulo */}
                    <p className="text-slate-500 text-center text-sm mb-4">
                        El evento será marcado como finalizado y la venta de boletos se cerrará. Esta acción no se puede deshacer.
                    </p>

                    {/* Resumen del evento */}
                    <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 mb-5">
                        <blockquote className="text-sm text-slate-600 border-l-2 border-blue-400 pl-3">
                            <p className="font-semibold text-slate-900 mb-0.5 truncate" title={eventName}>
                                {eventName}
                            </p>
                            {(eventDate || eventVenue) && (
                                <p className="text-xs text-slate-500 truncate">
                                    {eventDate}{eventDate && eventVenue && ' • '}{eventVenue}
                                </p>
                            )}
                        </blockquote>
                    </div>

                    {/* Botones */}
                    <div className="flex gap-3">
                        <button
                            onClick={onClose}
                            disabled={isFinishing}
                            className="flex-1 py-2.5 px-4 bg-white border-2 border-slate-200 hover:border-slate-300 text-slate-700 font-medium rounded-xl transition-colors disabled:opacity-50"
                        >
                            Cancelar
                        </button>
                        <button
                            onClick={onConfirm}
                            disabled={isFinishing}
                            className="flex-1 py-2.5 px-4 bg-blue-500 hover:bg-blue-600 text-white font-medium rounded-xl transition-colors shadow-lg shadow-blue-500/20 disabled:opacity-50"
                        >
                            {isFinishing ? 'Finalizando...' : 'Finalizar'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
